import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminPreAuthLayout from '../layouts/AdminPreAuthLayout';
import PreAuthCard from '../../shared/components/PreAuthCard';
import FormInput from '../../shared/components/FormInput';
import IconButton from '../../shared/components/IconButton';
import { API_URL } from '../../config/env';

export default function PasswordResetPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');

  const [form, setForm] = useState({
    password: '',
    password_confirmation: ''
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    if (!token) return;
    // Fetch user info for prepopulating email
    fetch(`${API_URL}/v1/admin/users/password/edit.json?reset_password_token=${encodeURIComponent(token)}`)
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(data => setUserEmail(data.email || ''))
      .catch(() => setUserEmail(''));
  }, [token]);

  const handleChange = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (form.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    if (form.password_confirmation !== form.password) {
      newErrors.password_confirmation = 'Passwords do not match';
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/v1/admin/users/password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user: {
            reset_password_token: token,
            password: form.password,
            password_confirmation: form.password_confirmation
          }
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Unable to reset password');
      }
      navigate('/admin/login');
    } catch (err) {
      setErrors({ general: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <AdminPreAuthLayout>
        <div className="alert alert-danger">Invalid or missing reset token.</div>
      </AdminPreAuthLayout>
    );
  }

  return (
    <AdminPreAuthLayout>
      <PreAuthCard>
        <div className="text-center mb-3">
          <h1 className="fs-4 fw-semibold mb-1">Reset your password</h1>
          <p className="text-muted mb-0">
            Enter your new password below to regain access.
          </p>
        </div>

        {errors.general && <div className="alert alert-danger py-2 small">{errors.general}</div>}

        <form onSubmit={handleSubmit} autoComplete="on">
          {/* Hidden email field for browser autofill */}
          <input
            type="email"
            name="email"
            autoComplete="username"
            style={{ display: 'none' }}
            tabIndex={-1}
            value={userEmail}
            readOnly
          />
          <FormInput
            id="new-password"
            label="New password"
            type="password"
            name="new-password"
            autoComplete="new-password"
            placeholder="Enter new password"
            value={form.password}
            onChange={e => handleChange('password', e.target.value)}
            icon="bi-lock-fill"
            error={errors.password}
            inputClassName="py-3 rounded-3 border fw-medium"
            required
          />

          <FormInput
            id="confirm-password"
            label="Confirm password"
            type="password"
            name="confirm-password"
            autoComplete="new-password"
            placeholder="Re-enter new password"
            value={form.password_confirmation}
            onChange={e => handleChange('password_confirmation', e.target.value)}
            icon="bi-lock-fill"
            error={errors.password_confirmation}
            inputClassName="py-3 rounded-3 border fw-medium"
            required
          />

          <IconButton
            type="submit"
            className="btn btn-primary w-100 py-3 fw-bold"
            icon="bi-check2"
            disabled={submitting}
          >
            Reset password
          </IconButton>
        </form>
      </PreAuthCard>
    </AdminPreAuthLayout>
  );
}
